import Timer from './timer.js';
import { v4 as uuidv4 } from 'uuid';

class Task {
    constructor({
        id,
        user_id,
        creation_date,
        title,
        parent_id,
        category,
        category_id = null,
        category_color = null,
        complete,
        frequency,
        working_time,
        importance,
        priority,
        due_date,
        duration,
        streak,
        assigned,
        shared_from_username = null,
        alarm,
        order = 0,
        ordered = false,
        level = 0, // Default level for the task
        subtasks = [],
        assigned_order = null,
    }) {
        this.id = id;
        this.user_id = user_id;
        this.creation_date = creation_date ? new Date(creation_date) : null;
        this.title = title;
        this.parent_id = parent_id;
        this.category = category;
        this.category_id = category_id;
        this.category_color = category_color;
        this.complete = complete ? new Date(complete) : null;
        this.frequency = frequency;
        this.working_time = parseInt(working_time,10);
        this.importance = parseInt(importance,10);
        this.priority = priority;
        this.duration = duration;
        this.streak = streak;
        this.assigned = assigned;
        this.shared_from_username = shared_from_username;
        this.order = parseInt(order, 10);
        this.due_date = due_date ? new Date(due_date) : null;
        this.subtasks = subtasks.map(subtask => new Task(subtask)).sort((a, b) => a.order - b.order);
        this.alarm = alarm;
        this.ordered = ordered;
        this.level = level;
        this.assigned_order=assigned_order;
    }
  
    // Method to check if the task is complete
    isComplete() {
        return this.complete !== null;
    }
  
    // Method to get all leaf subtasks (tasks without further subtasks), with an optional filter for completion status
    getLeafSubtasks({ complete = null } = {}) {
        if (this.subtasks.length === 0) {
            // If there are no subtasks, return itself if it matches the completion filter
            if (complete === null || (complete === false && !this.isComplete()) || (complete === true && this.isComplete())) {
                return [this];
            }
            return [];
        }
        // Otherwise, recursively get leaf subtasks from all children
        return this.subtasks.flatMap(subtask => subtask.getLeafSubtasks({ complete }));
    }
  
    // Convert back to JSON format
    toJSON() {
        return {
            id: this.id,
            user_id: this.user_id,
            creation_date: this.creation_date ? this.creation_date.toISOString() : null,
            title: this.title,
            parent_id: this.parent_id,
            category: this.category? this.category : "no-category",
            category_id: this.category_id,
            category_color: this.category_color,
            complete: this.complete ? this.complete.toISOString() : null,
            frequency: this.frequency,
            working_time: this.working_time,
            importance: this.importance,
            due_date: this.due_date ? this.due_date.toISOString() : null,
            duration: this.duration,
            assigned: this.assigned,
            shared_from_username: this.shared_from_username,
            subtasks: this.subtasks.map(subtask => subtask.toJSON()),
            alarm: this.alarm,
            order: this.order,
            ordered: this.ordered
        };
    }

    toAssignedTasks(path = []) {
        const currentPath = [...path, this.title];
        if (this.subtasks.length === 0) {
            const base = this.toJSON();
            return [{ ...base, full_title: currentPath }];
        }
        return this.subtasks.flatMap(subtask => subtask.toAssignedTasks(currentPath));
    }

    toFlatNodes() {
        const base = {
            id: this.id,
            parent_id: this.parent_id,
            title: this.title,
            category: this.category ? this.category : "no-category",
            category_id: this.category_id,
            frequency: this.frequency,
            importance: this.importance,
            order: this.order,
            ordered: this.ordered,
            alarm: this.alarm,
            creation_date: this.creation_date ? this.creation_date.toISOString() : null,
            due_date: this.due_date ? this.due_date.toISOString() : null,
            duration: this.duration,
        };
        return [
            base,
            ...this.subtasks.flatMap(subtask => subtask.toFlatNodes()),
        ];
    }

    toFlatWritePayload() {
        const nodes = this.toFlatNodes();
        return {
            nodes,
        };
    }
  
    // Method to add a subtask to the current task
    addSubtask(subtaskData,index) {
        const newSubtask = new Task({
            ...subtaskData,
            parent_id: this.id, // Ensure the subtask's parent_id is set to this task's id
            order:index,
            ordered: subtaskData.ordered || false
        });

        this.subtasks.splice(index, 0, newSubtask);
        this.updateSubtaskOrder(); // Update order values
        this.subtasks.sort((a, b) => a.order - b.order); // Sort by order
    }
    getTask(taskId) {
        // Check if the current task matches the taskId
        if (this.id === taskId) {
            return this;
        }
  
        // Recursively check subtasks
        for (let subtask of this.subtasks) {
            const foundTask = subtask.getTask(taskId);
            if (foundTask) {
                return foundTask;
            }
        }
  
        return null; // Task not found
    }
  
    // Method to remove a subtask by its id
    removeSubtask(subtaskId) {
        this.subtasks = this.subtasks.filter(subtask => subtask.id !== subtaskId);
        this.updateSubtaskOrder(); // Update order values
        this.subtasks.sort((a, b) => a.order - b.order); // Sort by order
    }

    updateSubtaskOrder() {
        this.subtasks.forEach((sub, idx) => {
            sub.order = idx;
        });
    }
    updateAllSubtasksOrder() {
        this.updateSubtaskOrder();
        this.subtasks.forEach(subtask => {
            subtask.updateAllSubtasksOrder();
        });
    }
    updateTask(updatedTask) {
        // Check if the current task matches the updatedTask id
        if (this.id === updatedTask.id) {
            // Update the properties of the current task with the properties of updatedTask
            this.user_id = updatedTask.user_id;
            this.creation_date = updatedTask.creation_date ? new Date(updatedTask.creation_date) : null;
            this.title = updatedTask.title;
            this.parent_id = updatedTask.parent_id;
            this.category = updatedTask.category;
            this.category_id = updatedTask.category_id;
            this.category_color = updatedTask.category_color;
            this.complete = updatedTask.complete ? new Date(updatedTask.complete) : null;
            this.frequency = updatedTask.frequency;
            this.working_time = updatedTask.working_time;
            this.importance = updatedTask.importance;
            this.due_date = updatedTask.due_date ? new Date(updatedTask.due_date) : null;
  
            // Recursively update subtasks
            this.subtasks = updatedTask.subtasks.map(subtaskData => {
                const existingSubtask = this.getTask(subtaskData.id);
                if (existingSubtask) {
                    // Update the existing subtask
                    existingSubtask.updateTask(subtaskData);
                    return existingSubtask;
                } else {
                    // Add the new subtask if it doesn't exist
                    return new Task(subtaskData);
                }
            }).sort((a, b) => a.order - b.order); // Sort by order
            return true;
        }
  
        // If not found at the current level, search recursively in subtasks
        for (let subtask of this.subtasks) {
            if (subtask.updateTask(updatedTask)) {
                return true;
            }
        }
  
        return false; // Task not found
    }
    static calculatePriority(importance, dueDate, creationDate, referenceDate = new Date()) {
        const now = new Date(referenceDate);
        const due = dueDate ? new Date(dueDate) : null;
        const created = creationDate ? new Date(creationDate) : null;
        let numDays = 100;

        if (due && !Number.isNaN(due.getTime())) {
            numDays = (due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
        } else if (created && !Number.isNaN(created.getTime())) {
            const ageInWholeDays = Math.floor(
                (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)
            );
            numDays = 100 - ageInWholeDays;
        }

        numDays = Math.max(0, numDays);
        const normalizedImportance = Number(importance) || 1;
        const distance = Math.sqrt(
            Math.pow(numDays, 2) + Math.pow(10 - normalizedImportance, 2)
        );
        return Math.round(distance * 100000) / 100000;
    }

    static toUserViewPriority(rawPriority) {
        if (rawPriority === null || rawPriority === undefined || rawPriority === '') return null;
        const numericPriority = Number(rawPriority);
        if (!Number.isFinite(numericPriority) || numericPriority < 0) return null;
        return Math.min(10, Math.max(0, 100 / (numericPriority + 10)));
    }

    static formatUserViewPriority(rawPriority) {
        const userPriority = Task.toUserViewPriority(rawPriority);
        return userPriority === null ? 'Not available' : userPriority.toFixed(2);
    }

    getPriority(referenceDate = new Date()) {
        return Task.calculatePriority(
            this.importance,
            this.due_date,
            this.creation_date,
            referenceDate
        );
    }

    getUserViewPriority(referenceDate = new Date()) {
        return Task.toUserViewPriority(this.getPriority(referenceDate));
    }
    getParentTasksByLeafPriority() {
        // Get all leaf subtasks with their priority levels
        const leafSubtasks = this.getLeafSubtasks();
    
        // Map each leaf's priority to its parent task's ID
        const parentPriorityMap = new Map();
    
        leafSubtasks.forEach(leaf => {
            if(leaf.complete === null){
                const parentId = leaf.parent_id;
                const priority = leaf.getPriority();
        
                if (!parentPriorityMap.has(parentId)) {
                    parentPriorityMap.set(parentId, priority);
                } else {
                    // Only update if the new priority is higher (i.e., lower distance)
                    const currentPriority = parentPriorityMap.get(parentId);
                    if (priority < currentPriority) {
                        parentPriorityMap.set(parentId, priority);
                    }
                }
            }
        });
            // Convert the map to an array of objects with `parent_id` and `priority`
    const parentPriorities = Array.from(parentPriorityMap, ([parent_id, priority]) => ({ parent_id, priority }));
    // Sort by priority from highest to lowest (lowest distance first)
    parentPriorities.sort((a, b) => a.priority - b.priority);
    console.log(parentPriorities);

    return parentPriorities;
    }

    // Method to duplicate a task and all its subtasks
    duplicate(newParentId = null) {
        // Create a new ID for the duplicated task
        const newId = uuidv4();
        
        // Create a deep copy of the task with a new ID and optional new parent ID
        const duplicatedTask = new Task({
            id: newId,
            user_id: this.user_id,
            creation_date: new Date(), // Reset creation date to now
            title: `${this.title}`, // Indicate it's a copy
            parent_id: newParentId, // Set the new parent ID if provided
            category: this.category,
            category_id: this.category_id,
            category_color: this.category_color,
            complete: null, // Reset completion status
            frequency: this.frequency,
            working_time: 0, // Reset working time
            importance: this.importance,
            due_date: this.due_date ? new Date(this.due_date) : null,
            duration: this.duration,
            assigned: false, // Reset assigned status
            streak: 0, // Reset streak
            order: this.order,
            ordered: this.ordered,
            subtasks: [] // Start with empty subtasks, we'll add them next
        });
        
        // Recursively duplicate all subtasks, setting the new parent ID
        this.subtasks.forEach(subtask => {
            const duplicatedSubtask = subtask.duplicate(newId);
            duplicatedTask.subtasks.push(duplicatedSubtask);
        });
        duplicatedTask.subtasks.sort((a, b) => a.order - b.order); // Ensure sorted
        
        return duplicatedTask;
    }

    // Method to get all available tasks (for dropdown selection)
    static getAllTasks(tasks) {
        if (!Array.isArray(tasks)) {
            // If it's a single task, make it an array
            tasks = [tasks];
        }
        
        // Create a flat list of all tasks with their full path for display
        const getAllTasksRecursive = (task, path = '', result = []) => {
            const fullPath = path ? `${path} > ${task.title}` : task.title;
            
            result.push({
                id: task.id,
                title: fullPath,
                level: path.split('>').length
            });
            
            task.subtasks.forEach(subtask => {
                getAllTasksRecursive(subtask, fullPath, result);
            });
            
            return result;
        };
        
        // Start with empty result and process each task
        let result = [];
        tasks.forEach(task => {
            result = [...result, ...getAllTasksRecursive(task)];
        });
        
        return result;
    }

}
class AssignedTask {
    constructor({
        id,
        task_id,
        user_id,
        creation_date,
        title,
        category,
        category_id = null,
        category_color = null,
        complete,
        frequency,
        working_time,
        importance,
        parent_id,
        due_date,
        start,
        duration,
        alarm = false,
        full_title,
        start_time,
        order,  // Add alarm property with default false
        streak,
        level = 0
    }) {
        this.id = id;
        this.task_id = task_id;
        this.user_id = user_id;
        this.creation_date = creation_date ? new Date(creation_date) : null;
        this.title = title;
        this.full_title = full_title; // Store full title
        this.parent_id = parent_id;
        this.category = category;
        this.category_id = category_id;
        this.category_color = category_color;
        this.complete = complete ? new Date(complete) : null;
        this.frequency = frequency;
        this.duration =duration ? parseInt(duration,10):0;
        this.start = start ? new Date(start) : null;
        this.working_time = working_time? parseInt(working_time,10):0;
        this.importance = parseInt(importance,10);
        this.due_date = due_date ? new Date(due_date) : null;
        this.alarm = alarm;  // Store alarm property
        this.timer = new Timer(this.duration, this.working_time);
        this.start_time = start_time ? new Date(start_time) : null;
        this.order = parseInt(order, 10);
        this.streak = streak;
        this.level = parseInt(level, 10) || 0;
    }
    toJSON() {
        return {
            id: this.id,
            task_id: this.task_id,
            user_id: this.user_id,
            creation_date: this.creation_date ? this.creation_date.toISOString() : null,
            title: this.title,
            parent_id: this.parent_id,
            category: this.category,
            category_id: this.category_id,
            category_color: this.category_color,
            complete: this.complete ? this.complete.toISOString() : null,
            frequency: this.frequency,
            working_time: this.timer.workTime/1000,
            duration: this.duration,
            importance: this.importance,
            start: this.start ? this.start.toISOString() : null,
            due_date: this.due_date ? this.due_date.toISOString() : null,
            start_time: this.start_time ? this.start_time.toISOString() : null,
            order: this.order,
            level: this.level,
            alarm: this.alarm,
            full_title: this.full_title,
            streak: this.streak,
        };
    }
  
    // Method to check if the task is complete
    isComplete() {
        return this.complete !== null;
    }

    
}
  
  
export { AssignedTask };
export default Task;
